import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { clearFreelancerSession } from "../../../store/freelancerAuthSlice";
import { persistor } from "../../../store/store";
import { FREELANCER_LOGIN_PAGE } from "../../auth/routes";

import {
  FREELANCER_STORE_PAGE,
  FREELANCER_SCHEDULED_STORES_PAGE,
} from "../routes";

import AssignFreelancersToStoresIcon from "../../../img/user-plus.svg";
import ListSlotsIcon from "../../../img/calendar.svg";
import MyScheduleIcon from "../../../img/calendar.svg";
import EditProfileIcon from "../../../img/user-pen.svg";

// Mesmo background usado no Home "admin" (ajuste o caminho se necessário)
import DenseForest from "../../../img/dense-forest.jpg";

import AssignFreelancersToStoresModal from "../../../components/modal/assign-freelancers-to-stores/AssignFreelancersToStoresModal";
import EditFreelancerModal from "../../../components/modal/edit-freelancer/EditFreelancerModal";

import {
  getFreelancerMeApi,
  type FreelancerPublic,
} from "../../../views/freelancer/freelancerApi";

type MenuKey = "assign-stores" | "check-slots" | "my-schedule" | "edit-profile";

type MenuCard = {
  key: MenuKey;
  title: string;
  description: string;
  icon: string;
  onClick: () => void;
  hidden?: boolean;
};

const FreelancerHomePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [isAssignStoresModalOpen, setIsAssignStoresModalOpen] = useState(false);
  const [isEditFreelancerModalOpen, setIsEditFreelancerModalOpen] =
    useState(false);

  const [freelancerMe, setFreelancerMe] = useState<FreelancerPublic | null>(
    null,
  );
  const [loadingMe, setLoadingMe] = useState(false);

  useEffect(() => {
    if (!token) navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
  }, [token, navigate]);

  const handleLogout = async () => {
    dispatch(clearFreelancerSession());
    await persistor.purge();
    toast.success("Você saiu.");
    navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
  };

  // Carrega dados do freelancer logado (pra pré-preencher o modal de edição)
  useEffect(() => {
    const loadMe = async () => {
      if (!token) return;

      try {
        setLoadingMe(true);
        const me = await getFreelancerMeApi(token);
        setFreelancerMe(me);
      } catch (error) {
        toast.error("Erro ao carregar seus dados.");
      } finally {
        setLoadingMe(false);
      }
    };

    loadMe();
  }, [token]);

  const cards: MenuCard[] = useMemo(
    () => [
      {
        key: "edit-profile",
        title: "Editar meus dados",
        description: "Atualize suas informações pessoais e de pagamento.",
        icon: EditProfileIcon,
        onClick: () => setIsEditFreelancerModalOpen(true),
      },
      {
        key: "assign-stores",
        title: "Minhas lojas",
        description:
          "Assinale-se ou desassinale-se das lojas onde deseja trabalhar.",
        icon: AssignFreelancersToStoresIcon,
        onClick: () => setIsAssignStoresModalOpen(true),
      },
      {
        key: "check-slots",
        title: "Verificar horários disponíveis",
        description:
          "Veja as lojas disponíveis e consulte os horários para agendar.",
        icon: ListSlotsIcon,
        onClick: () => navigate(FREELANCER_STORE_PAGE()),
      },
      {
        key: "my-schedule",
        title: "Minha agenda",
        description:
          "Veja as lojas onde você já possui horários futuros agendados.",
        icon: MyScheduleIcon,
        onClick: () => navigate(FREELANCER_SCHEDULED_STORES_PAGE()),
      },
    ],
    [navigate],
  );

  if (!token) return null;

  return (
    <div className="homeLayout">
      <main className="homeMain">
        <section className="ecosystem">
          <img
            className="ecosystem__bg-img"
            src={DenseForest}
            alt=""
            aria-hidden="true"
          />
          <div className="ecosystem__content-bg" aria-hidden="true" />

          <div className="ecosystem__content">
            <header className="ecosystem__header">
              <h1 className="ecosystem__title">
                <span className="ecosystem__title-strong">Eco System </span>
                <span className="ecosystem__title-light">Freelancer</span>
              </h1>

              <p className="ecosystem__subtitle">Escolha uma opção</p>

              <div className="ecosystem__actions">
                <button
                  className="ecosystem__actionBtn"
                  onClick={handleLogout}
                >
                  Deslogar
                </button>
              </div>
            </header>

            <div className="ecosystem__cards" role="list">
              {cards
                .filter((c) => !c.hidden)
                .map((card) => (
                  <article
                    key={card.key}
                    className="ecosystem__card"
                    role="listitem"
                  >
                    <div className="ecosystem__cardTop">
                      <img
                        className="ecosystem__cardIcon"
                        src={card.icon}
                        alt=""
                      />

                      <div className="ecosystem__cardText">
                        <h2 className="ecosystem__cardTitle">{card.title}</h2>
                        <p className="ecosystem__cardDesc">
                          {card.description}
                        </p>
                      </div>
                    </div>

                    <button
                      className="ecosystem__cardBtn"
                      onClick={card.onClick}
                      disabled={card.key === "edit-profile" && loadingMe}
                      title={
                        card.key === "edit-profile" && loadingMe
                          ? "Carregando seus dados..."
                          : ""
                      }
                    >
                      {card.key === "edit-profile" && loadingMe
                        ? "Carregando..."
                        : "Acessar"}
                    </button>
                  </article>
                ))}
            </div>
          </div>
        </section>

        <AssignFreelancersToStoresModal
          isOpen={isAssignStoresModalOpen}
          onClose={() => setIsAssignStoresModalOpen(false)}
        />

        <EditFreelancerModal
          isOpen={isEditFreelancerModalOpen}
          onClose={() => setIsEditFreelancerModalOpen(false)}
          freelancer={freelancerMe}
          onUpdated={(updated) => {
            setFreelancerMe(updated);
          }}
        />
      </main>
    </div>
  );
};

export default FreelancerHomePage;
